import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import { RecordCard } from '../components/mylife/RecordCard';
import { RecordDetail } from '../components/mylife/RecordDetail';
import { NotesThread } from '../components/notes/NotesThread';
import { useLazyRecords } from '../hooks/useLazyRecords';
import { authApi, organisationsApi } from '../lib/api';
import { PLACEMENT_TYPES, CARER_LED_TYPES, respitePatternLabel } from '../lib/orgTypes';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ym = (v) => {
  const m = /^(\d{4})-(\d{2})/.exec(v || '');
  return m ? { y: +m[1], m: +m[2] } : null;
};
const monthYear = (v) => {
  const d = ym(v);
  return d ? `${MONTHS[d.m - 1]} ${d.y}` : null;
};

/** "About 2 years 5 months" from YYYY-MM[-DD] dates; null if either is missing. */
export function durationWords(start, end) {
  const a = ym(start);
  const b = ym(end);
  if (!a || !b) return null;
  const months = (b.y - a.y) * 12 + (b.m - a.m);
  if (months < 0) return null;
  if (months < 1) return 'Less than a month';
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [y ? `${y} year${y === 1 ? '' : 's'}` : null, m ? `${m} month${m === 1 ? '' : 's'}` : null].filter(Boolean);
  return `About ${parts.join(' ')}`;
}

export const placementName = (p) => p?.name || (p?.carer_names?.length ? p.carer_names.join(' and ') : 'A placement');

/**
 * The records from while the person lived there. Mounted only once the
 * placement (and so its dates) is known, because the records hook reads its
 * filter once. Before decryption a record only shows its year, so records
 * from the years the placement spans are kept; once decrypted, only those
 * whose date falls inside it (the server's `placement_id`) stay.
 */
const PlacementRecords = ({ placement, onOpen }) => {
  const a = ym(placement.start_date);
  const b = ym(placement.end_date);
  const filter = useCallback((r) => {
    if (!r.pending) return r.placement_id === placement.id;
    if (!a || !b) return false;
    return r.year == null || (r.year >= a.y && r.year <= b.y);
  }, [placement.id, a?.y, b?.y]); // eslint-disable-line react-hooks/exhaustive-deps
  const { records: lazy, onVisible, loading } = useLazyRecords({ filter });
  const records = useMemo(() => lazy.filter((r) => r.pending || r.placement_id === placement.id), [lazy, placement.id]);
  const settled = records.filter((r) => !r.pending);

  return (
    <section className="space-y-3" aria-labelledby="placement-records">
      <h2 id="placement-records" className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-ink">
        Records from while you lived here{settled.length && settled.length === records.length ? ` (${settled.length})` : ''}
      </h2>
      {loading && !records.length ? (
        <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Finding them…</p>
      ) : records.length === 0 ? (
        <p className="text-[15px] text-ink-55" data-testid="placement-no-records">
          No records are dated within this placement yet. They appear here once the documents that mention it have been analysed — and only if the placement’s dates are known.
        </p>
      ) : (
        <div className="space-y-3" data-testid="placement-records-list">
          {records.map((r) => <RecordCard key={r.id} record={r} onClick={() => onOpen(r.id)} onVisible={onVisible} />)}
        </div>
      )}
    </section>
  );
};

/**
 * One placement — where the person lived — in its own right: the details
 * (who, where, when, how long, who arranged it, short breaks), the records
 * from the time they lived there, and their own notes on the place and on
 * each carer. A placement that is its own organisation (a children's home
 * run by a company) is shown on that organisation's page instead.
 * Details are edited in the organisation's edit dialog — one editable copy.
 */
export const PlacementPage = () => {
  const { orgId, placementId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(false);
  const [respitePool, setRespitePool] = useState([]);
  const [openRecord, setOpenRecord] = useState(null);

  useEffect(() => {
    organisationsApi.getDashboard(orgId)
      .then((d) => setOrg(d.data?.organisation || null))
      .catch(() => setError(true));
    authApi.getMe().then((me) => setRespitePool(me?.data?.respite_carers || [])).catch(() => {});
  }, [orgId]);

  const placement = useMemo(() => (org?.placements || []).find((p) => p.id === placementId) || null, [org, placementId]);

  // Its own organisation's page is the single source of truth.
  useEffect(() => {
    if (placement?.linked_org_id) navigate(`/organisations/${placement.linked_org_id}`, { replace: true });
  }, [placement, navigate]);


  if (error || (org && !placement)) {
    return (
      <AppShell>
        <main className="space-y-3">
          <p className="text-[15px] text-ink-70" data-testid="placement-missing">We couldn’t find that placement. It may have been removed.</p>
          <Link to="/organisations" className="text-sm font-semibold text-brand hover:text-brand-ink">Back to your organisations</Link>
        </main>
      </AppShell>
    );
  }
  if (!org || !placement) {
    return (
      <AppShell>
        <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Opening…</p>
      </AppShell>
    );
  }

  const p = placement;
  const typeLabel = PLACEMENT_TYPES.find((t) => t.value === p.type)?.label;
  const from = monthYear(p.start_date);
  const to = monthYear(p.end_date);
  const howLong = durationWords(p.start_date, p.end_date);
  const carers = (p.carer_names || []).filter(Boolean);
  const breaks = (p.respite || []).map((e) => {
    const c = respitePool.find((r) => r.id === e.carer_id);
    return c ? { name: c.name, pattern: respitePatternLabel(e.pattern), from: monthYear(e.from), to: monthYear(e.to), notes: e.notes } : null;
  }).filter(Boolean);

  const Fact = ({ label, children }) => (
    <div className="space-y-0.5">
      <dt className="text-sm text-ink-55">{label}</dt>
      <dd className="text-[16px] text-ink">{children}</dd>
    </div>
  );

  return (
    <AppShell>
      <main className="space-y-8" data-testid="placement-page">
        <PageHeader
          eyebrow={(
            <>
              {typeLabel || 'Placement'}
              <span aria-hidden="true"> · </span>
              <Link to={`/organisations/${org.id}`} className="normal-case hover:text-ink">arranged by {org.name}</Link>
            </>
          )}
          title={placementName(p)}
          lede={(from || to) && (
            <p>
              {from && to ? `${from} to ${to}` : from ? `From ${from}` : `Until ${to}`}
              {howLong && <> · {howLong.toLowerCase()}</>}
            </p>
          )}
        />

        <section className="rounded-lg bg-ground p-5 space-y-4" aria-labelledby="placement-details">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="placement-details" className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-ink">About this placement</h2>
            <Link to={`/organisations?edit=${org.id}`} className="text-sm font-semibold text-brand hover:text-brand-ink shrink-0" data-testid="placement-edit">
              Change these details
            </Link>
          </div>
          <dl className="grid sm:grid-cols-2 gap-4">
            {carers.length > 0 && <Fact label={CARER_LED_TYPES.has(p.type) ? 'Carers' : 'People'}>{carers.join(', ')}</Fact>}
            {p.address && <Fact label="Where">{p.address}</Fact>}
            <Fact label="Arranged by">
              <Link to={`/organisations/${org.id}`} className="text-brand hover:text-brand-ink">{org.name}</Link>
            </Fact>
            {howLong && <Fact label="How long">{howLong}</Fact>}
          </dl>
          {breaks.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-[15px] font-semibold text-ink">Short breaks while you lived here</h3>
              <ul className="space-y-1.5">
                {breaks.map((b, i) => (
                  <li key={i} className="text-[16px] text-ink">
                    {b.name}
                    {b.pattern && <span className="text-ink-55"> · {b.pattern.toLowerCase()}</span>}
                    {(b.from || b.to) && <span className="text-ink-55"> · {[b.from, b.to].filter(Boolean).join(' to ')}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!carers.length && !p.address && !breaks.length && (
            <p className="text-[15px] text-ink-55">Not much is written down about this placement yet. You can add who looked after you and where on the organisation's page.</p>
          )}
        </section>

        <PlacementRecords placement={p} onOpen={setOpenRecord} />

        <section className="rounded-lg bg-ground p-5">
          <NotesThread
            kind="memory" targetType="placement" targetId={p.id} orgId={org.id}
            heading="My notes"
            prompt="Anything about living here — the house, the people, a routine, how it felt, or what the records get wrong."
            placeholder="Write as much or as little as you like…"
            testId="placement-my-notes"
          />
        </section>

        {carers.length > 0 && (
          <section className="space-y-3">
            <h2 className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-ink">{CARER_LED_TYPES.has(p.type) ? 'Your carers' : 'People'}</h2>
            {carers.map((name) => (
              <div key={name} className="rounded-lg bg-ground p-5">
                <NotesThread
                  targetType="person" personName={name} orgId={org.id} compact
                  heading={name}
                  prompt="What they were like, what you remember of them, or anything the records get wrong about them."
                  testId={`carer-notes-${name.replace(/\W+/g, '-').toLowerCase()}`}
                />
              </div>
            ))}
          </section>
        )}
      </main>

      <RecordDetail
        recordId={openRecord}
        open={!!openRecord}
        onOpenChange={(o) => !o && setOpenRecord(null)}
        onOpenDocument={(docId, page) => navigate(`/documents?open=${docId}${page ? `&page=${page}` : ''}`)}
      />
    </AppShell>
  );
};

export default PlacementPage;
